import styled from 'styled-components';
import { LearnLevel } from '../../../../../common/src/model.ts';
import { howToSaySyllablesText, SyllablesLink } from '../interval/interval_start_screen.tsx';
import { PrimaryButton } from '../learn_buttons.tsx';
import { LevelChips } from '../learn_level_chips.tsx';
import { ScreenBody, ScreenSubtitle, ScreenTitle, shortScreen, Slack } from '../learn_screen_parts.tsx';
import { singAlongGuideLabel, singAlongGuideNote, SingAlongSwitch } from '../learn_sing_along_switch.tsx';

// The Sing in Solfège start screen (issue #3722). The canvas has no artboard for it; it is needed
// because opening the microphone needs a tap, so it takes the shape of its neighbours: Interval
// Training's title and line about the exercise, the shared level chips (only Easy is built, so the
// page passes it alone as selectable and an unlocked Medium or Hard says "Coming soon", as Interval
// Training's chips did before its later levels), Pitch Match's sing-along switch showing this
// module's own stored choice (per device, off until the singer turns it on), Interval Training's
// link to Meet the syllables, and Start.
// Start is the user gesture that opens audio, so it is a real button and nothing starts on its own.
//
// Presentational: the page supplies the level, the chips' levels, the device's sing-along choice
// and every handler. No key name, no letter names, no lyrics.

export const solfegeStartScreenId = 'solfege-start';
export const solfegeStartButtonId = 'solfege-start-button';
export const solfegeSyllablesLinkId = 'solfege-syllables-link';
export const solfegeSingAlongSwitchId = 'solfege-sing-along-switch';

export const solfegeStartTitle = 'Hear a hymn, sing it in syllables';
export const solfegeStartSubtitle =
  'Listen to the opening phrase of a hymn. Then sing it back in syllables, in time with the dot.';

export const SolfegeStartScreen = ({
  level,
  unlocked,
  selectable,
  singAlong,
  onSelectLevel,
  onToggleSingAlong,
  onOpenSyllables,
  onStart,
}: {
  level: LearnLevel;
  unlocked: LearnLevel[];
  selectable: LearnLevel[]; // what this build plays: Easy alone for now
  singAlong: boolean; // the device's stored choice for this module, or off
  onSelectLevel: (level: LearnLevel) => void;
  onToggleSingAlong: (singAlong: boolean) => void; // the value the switch was moved to
  onOpenSyllables: () => void; // "How to say the syllables"
  onStart: () => void;
}) => (
  <ScreenBody id={solfegeStartScreenId}>
    <ScreenTitle>{solfegeStartTitle}</ScreenTitle>
    <ScreenSubtitle>{solfegeStartSubtitle}</ScreenSubtitle>
    <LevelChips level={level} unlocked={unlocked} selectable={selectable} onSelectLevel={onSelectLevel} />
    <SingAlongSwitch
      id={solfegeSingAlongSwitchId}
      label={singAlongGuideLabel}
      note={singAlongGuideNote}
      singAlong={singAlong}
      onToggleSingAlong={onToggleSingAlong}
    />
    <SyllablesLinkBelowSwitch
      id={solfegeSyllablesLinkId}
      data-testid={solfegeSyllablesLinkId}
      type="button"
      onClick={onOpenSyllables}
    >
      {howToSaySyllablesText}
    </SyllablesLinkBelowSwitch>
    <Slack />
    <PrimaryButton id={solfegeStartButtonId} data-testid={solfegeStartButtonId} onClick={onStart}>
      Start
    </PrimaryButton>
  </ScreenBody>
);

// Interval Training's link to Meet the syllables, with the air the switch's note wants above it
const SyllablesLinkBelowSwitch = styled(SyllablesLink)`
  margin-top: 18px;

  ${shortScreen} {
    margin-top: 10px;
  }
`;
